import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trophy,
  Calendar,
  Clock,
  Filter,
  Search,
  ChevronRight,
  Play,
  Pause,
  Target,
  Users,
  ArrowRight,
  Activity,
  Zap,
  Star,
  MapPin,
  CalendarDays
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/datepicker.css';
import { gamesApi } from '../../services/api';
import { useSite } from '../../contexts/SiteContext';
import { addDays, startOfWeek, endOfWeek, format } from 'date-fns';
import { LoadingSpinner, EmptyStates, GameCardSkeleton } from '../Common';

// Types for game scores
interface Score {
  id: string;
  home_team: {
    name: string;
    city: string;
    abbreviation: string;
    sport_name: string;
  };
  away_team: {
    name: string;
    city: string;
    abbreviation: string;
    sport_name: string;
  };
  home_score: number;
  away_score: number;
  status: 'scheduled' | 'live' | 'final' | 'postponed';
  game_date: string;
  game_time?: string;
  quarter?: string;
  time_remaining?: string;
  championship_name?: string;
  season_name?: string;
  venue?: string;
}

const ScoresPage: React.FC = () => {
  const site = useSite();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSport, setSelectedSport] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  
  // Initialize date based on sport
  const getDefaultDateRange = () => {
    const today = new Date();
    const sportLower = site.sport?.toLowerCase();
    
    // Football defaults to current week
    if (sportLower === 'football') {
      return {
        startDate: startOfWeek(today, { weekStartsOn: 1 }), // Monday
        endDate: endOfWeek(today, { weekStartsOn: 1 }) // Sunday
      };
    }
    
    // All other sports default to today
    return {
      startDate: today,
      endDate: today
    };
  };
  
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [startDate, setStartDate] = useState(dateRange.startDate);
  const [endDate, setEndDate] = useState(dateRange.endDate);
  
  // Update dates when sport changes
  useEffect(() => {
    const newRange = getDefaultDateRange();
    setStartDate(newRange.startDate);
    setEndDate(newRange.endDate);
  }, [site.sport]);

  // Fetch games based on selected date range
  const { data: gamesData, isLoading, error } = useQuery({
    queryKey: ['games', site.sport, startDate, endDate],
    queryFn: async () => {
      try {
        // Format dates for API
        const startDateStr = format(startDate, 'yyyy-MM-dd');
        const endDateStr = format(endDate, 'yyyy-MM-dd');

        // Use the date range API without season filter to get all games
        const response = await gamesApi.getByDateRange(startDateStr, endDateStr);
        return response.data || [];
      } catch (error) {
        console.error('Error fetching games:', error);
        // Fallback to getting all games if date range fails
        try {
          const allResponse = await gamesApi.getAll();
          return allResponse.data || [];
        } catch {
          return [];
        }
      }
    },
  });

  // Transform API games to scores format
  const scores: Score[] = gamesData?.map((game: any) => ({
    id: game.id || game.slug,
    home_team: {
      name: game.home_team?.name || game.home_team_name || '',
      city: game.home_team?.city || game.home_team_city || '',
      abbreviation: game.home_team?.abbreviation || game.home_team_abbreviation || '',
      sport_name: game.home_team?.sport?.name || game.home_team?.sport_name || site.sport || ''
    },
    away_team: {
      name: game.away_team?.name || game.away_team_name || '',
      city: game.away_team?.city || game.away_team_city || '',
      abbreviation: game.away_team?.abbreviation || game.away_team_abbreviation || '',
      sport_name: game.away_team?.sport?.name || game.away_team?.sport_name || site.sport || ''
    },
    home_score: game.home_score ?? 0,
    away_score: game.away_score ?? 0,
    status: game.status || 'scheduled',
    game_date: game.game_date || new Date().toISOString().split('T')[0],
    game_time: game.game_time,
    quarter: game.quarter || game.period,
    time_remaining: game.time_remaining,
    championship_name: game.championship?.name || game.championship_name,
    season_name: game.season?.name || game.season_name || '',
    venue: game.venue?.name || game.venue_name || ''
  })) || [];

  const finalScores = scores;

  const filteredScores = finalScores?.filter((score: Score) => {
    const matchesSearch = 
      score.home_team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      score.away_team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      score.home_team.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      score.away_team.city.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSport = selectedSport === 'all' || score.home_team.sport_name.toLowerCase() === selectedSport;
    const matchesStatus = selectedStatus === 'all' || score.status === selectedStatus;
    return matchesSearch && matchesSport && matchesStatus;
  }) || [];

  const sports = Array.from(new Set(finalScores?.map(s => s.home_team.sport_name) || []));
  const liveGames = filteredScores.filter(score => score.status === 'live');
  const finalGames = filteredScores.filter(score => score.status === 'final');
  const scheduledGames = filteredScores.filter(score => score.status === 'scheduled');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live': return 'text-success';
      case 'final': return 'text-gray-400';
      case 'scheduled': return 'text-primary';
      case 'postponed': return 'text-warning';
      default: return 'text-gray-400';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'live': return 'badge-success';
      case 'final': return 'badge-secondary';
      case 'scheduled': return 'badge-primary';
      case 'postponed': return 'badge-warning';
      default: return 'badge-secondary';
    }
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen py-12">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full border border-primary/30 mb-6"
            >
              <Activity className="w-4 h-4 text-primary" />
              <span className="text-primary text-xs font-semibold uppercase tracking-wider">
                Live Scores & Results
              </span>
            </motion.div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="text-white">GAME </span>
              <span className="neon-text">SCORES</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Live scores, recent results, and upcoming games across all major sports leagues
            </p>
          </div>

          {/* Filters */}
          <div className="glass-card p-6 mb-8">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search teams..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-surface/50 border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary transition-colors"
                />
              </div>
              
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="relative flex-1 lg:flex-none lg:min-w-[160px]">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedSport}
                    onChange={(e) => setSelectedSport(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="all">All Sports</option>
                    {sports.map(sport => (
                      <option key={sport} value={sport?.toLowerCase()}>
                        {sport}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative flex-1 lg:flex-none lg:min-w-[160px]">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="all">All Games</option>
                    <option value="live">Live</option>
                    <option value="final">Final</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="postponed">Postponed</option>
                  </select>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                  <div className="relative">
                    <CalendarDays className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10 pointer-events-none" />
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => {
                        if (date) {
                          setStartDate(date);
                          // If selecting a single day, set end date to same day
                          if (date.toDateString() === startDate.toDateString()) {
                            setEndDate(date);
                          }
                        }
                      }}
                      selectsStart
                      startDate={startDate}
                      endDate={endDate}
                      dateFormat="MMM d, yyyy"
                      className="pl-10 pr-4 py-3 bg-surface/50 border border-border rounded-lg text-white cursor-pointer focus:outline-none focus:border-primary transition-colors w-full sm:w-40"
                      placeholderText="Start Date"
                      popperClassName="react-datepicker-popper"
                      calendarClassName="!bg-surface !border-border"
                      dayClassName={() => "hover:!bg-primary/20"}
                      monthClassName={() => "!text-white"}
                      weekDayClassName={() => "!text-gray-400"}
                      popperProps={{
                        strategy: "fixed"
                      }}
                      portalId="root"
                    />
                  </div>
                  <span className="text-gray-400">to</span>
                  <div className="relative">
                    <CalendarDays className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10 pointer-events-none" />
                    <DatePicker
                      selected={endDate}
                      onChange={(date) => date && setEndDate(date)}
                      selectsEnd
                      startDate={startDate}
                      endDate={endDate}
                      minDate={startDate}
                      dateFormat="MMM d, yyyy"
                      className="pl-10 pr-4 py-3 bg-surface/50 border border-border rounded-lg text-white cursor-pointer focus:outline-none focus:border-primary transition-colors w-full sm:w-40"
                      placeholderText="End Date"
                      popperClassName="react-datepicker-popper"
                      calendarClassName="!bg-surface !border-border"
                      dayClassName={() => "hover:!bg-primary/20"}
                      monthClassName={() => "!text-white"}
                      weekDayClassName={() => "!text-gray-400"}
                      popperProps={{
                        strategy: "fixed"
                      }}
                      portalId="root"
                    />
                  </div>
                  <button
                    onClick={() => {
                      const today = new Date();
                      setStartDate(today);
                      setEndDate(today);
                    }}
                    className="px-4 py-3 bg-surface/50 border border-border rounded-lg text-white hover:border-primary transition-colors text-sm"
                  >
                    Today
                  </button>
                  <button
                    onClick={() => {
                      const today = new Date();
                      setStartDate(startOfWeek(today, { weekStartsOn: 1 }));
                      setEndDate(endOfWeek(today, { weekStartsOn: 1 }));
                    }}
                    className="px-4 py-3 bg-surface/50 border border-border rounded-lg text-white hover:border-primary transition-colors text-sm"
                  >
                    This Week
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6 text-center"
            >
              <Zap className="w-8 h-8 text-success mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {liveGames.length}
              </div>
              <div className="text-sm text-gray-400">Live Games</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-card p-6 text-center"
            >
              <Trophy className="w-8 h-8 text-primary mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {finalGames.length}
              </div>
              <div className="text-sm text-gray-400">Final Scores</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass-card p-6 text-center"
            >
              <Clock className="w-8 h-8 text-secondary mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {scheduledGames.length}
              </div>
              <div className="text-sm text-gray-400">Upcoming</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="glass-card p-6 text-center"
            >
              <Users className="w-8 h-8 text-accent mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {sports.length}
              </div>
              <div className="text-sm text-gray-400">Sports</div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Scores Grid */}
      <div className="max-w-7xl mx-auto">
        {isLoading ? (
          <GameCardSkeleton count={8} />
        ) : error ? (
          <EmptyStates.LoadingError
            error={(error as Error).message || 'Failed to load game scores'}
            onRetry={() => window.location.reload()}
          />
        ) : filteredScores.length === 0 ? (
          <EmptyStates.NoGames />
        ) : (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {filteredScores.map((score) => (
              <motion.div
                key={score.id}
                variants={item}
                whileHover={{ scale: 1.02 }}
                className="group"
              >
                <Link 
                  to={`/scores/${score.id}`}
                  className="block h-full"
                >
                  <div className="glass-card h-full overflow-hidden border border-border hover:border-primary/50 transition-all duration-300 cursor-pointer group-hover:shadow-[0_0_30px_rgba(0,255,136,0.15)]">
                    {/* Game Header */}
                  <div className="p-6 border-b border-border/50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <span className="badge badge-primary text-xs">
                          {score.home_team.sport_name}
                        </span>
                        {score.status === 'live' && (
                          <span className="flex items-center space-x-1 text-success text-xs">
                            <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
                            <span className="font-bold">LIVE</span>
                          </span>
                        )}
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
                    </div>

                    {/* Teams and Score */}
                    <div className="space-y-4">
                      {/* Away Team */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-700 to-gray-800 border border-border flex items-center justify-center">
                            <span className="text-sm font-bold text-white">
                              {score.away_team.abbreviation}
                            </span>
                          </div>
                          <div>
                            <div className="font-bold text-white">
                              {score.away_team.name}
                            </div>
                            <div className="text-xs text-gray-500">Away</div>
                          </div>
                        </div>
                        <div className="text-3xl font-black text-white">
                          {score.status === 'scheduled' ? '-' : score.away_score}
                        </div>
                      </div>

                      {/* Home Team */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/30 flex items-center justify-center">
                            <span className="text-sm font-bold text-primary">
                              {score.home_team.abbreviation}
                            </span>
                          </div>
                          <div>
                            <div className="font-bold text-white">
                              {score.home_team.name}
                            </div>
                            <div className="text-xs text-gray-500">Home</div>
                          </div>
                        </div>
                        <div className="text-3xl font-black text-white">
                          {score.status === 'scheduled' ? '-' : score.home_score}
                        </div>
                      </div>
                    </div>

                    {/* Game Status */}
                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className={`badge ${getStatusBadge(score.status)} text-xs`}>
                          {score.status === 'live' && score.quarter && score.time_remaining
                            ? `${score.quarter} - ${score.time_remaining}`
                            : score.status === 'scheduled' && score.game_time
                            ? score.game_time
                            : score.status.charAt(0).toUpperCase() + score.status.slice(1)
                          }
                        </span>
                        {score.championship_name && (
                          <Star className="w-4 h-4 text-primary" />
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(score.game_date).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ScoresPage;